// import react packages;
import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useMutation } from "@apollo/client";
import PropTypes from "prop-types";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// Import Polaris Components
import {
  LegacyCard as Card,
  Collapsible,
  Checkbox,
  Button,
  PageActions,
  Link,
  TextField,
  InlineStack,
  BlockStack,
  Box,
  Text,
  Divider,
} from "@shopify/polaris";

// helper
import { PrivateContext } from "lib/context";
import { baseHelper, errorHelper } from "lib/helpers";
import {
  applySavedChecklist,
  computeChecklistUpdate,
  extractChecklistState,
} from "lib/helpers/connectShopChecklist";

import constant from "lib/constant/constant";

import validate from "app/appPermission/generic/yup";

// import components
import SyncProduct from "app/product/modules/provider/features/add/subFeatures/syncProduct";
import TermModal from "app/product/modules/provider/features/genric/term/term";
import DiscoveryTermModal from "app/product/modules/provider/features/genric/term/discoveryTerm";
import { useLocation, useNavigate } from "react-router-dom";
import { CopyButton } from "lib/components";
import NEW_APP_DETAILS from "app/appPermission/apollo/mutation/newAppDetails";
import UPDATE_CONNECT_SHOP_CHECKLIST from "app/appPermission/apollo/mutation/updateConnectShopChecklist";
import ConnectedShop from "./subFeatureItems/connected";

const VendorAppConnectShopify = (props) => {
  const { currentUser: currentUserData, cms, currentUserRefetch } = useContext(PrivateContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { appToMigrate = false, pageName, setBanner, setIsOpenModal, setModalBanner } = props;
  const [shop, setShop] = useState((currentUserData && currentUserData.shop) || "");
  const [isConnected, setIsConnected] = useState(!!(currentUserData && currentUserData.shop && currentUserData.shopifyAccessToken));
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [shopConnectedStatus, setShopConnectedStatus] = useState(!!isConnected);
  const [disconnectStatus, setDisconnectStatus] = useState(false);
  const [distributionMode, setDistributionMode] = useState("normal");
  const {
    ADMIN_SCOPES_KEY,
    API_APP_KEY,
    API_APP_SECRET_KEY,
    HIDE,
    REGISTERSUBDOMIN,
    SHOPIFY_CONNECT_FORM,
    STEP,
    SETTINGS,
    SHOW,
    TOTAL_CHECKBOXES,
    INSTALL_APP_KEY,
    SCOPE_VALUES,
    APP_URL_VALUE,
    REDIRECT_URL_VALUE,
  } = constant;
  const [newAppDetails, { loading }] = useMutation(NEW_APP_DETAILS);
  const [saveConnectShopChecklist] = useMutation(UPDATE_CONNECT_SHOP_CHECKLIST);
  const saveChecklistTimeoutRef = useRef(null);
  const currentUri = window.location.hostname;

  const defaultPermissions = {
    goListingsPage: {
      checked: false,
      disable: false,
      order: 1,
      type: STEP,
      title: cms("common.appPermission.label.goListingsPage"),
    },
    developApps: {
      checked: false,
      disable: true,
      order: 2,
      type: STEP,
      title: cms("common.appPermission.label.developApps"),
    },
    customAppDev: {
      checked: false,
      disable: true,
      order: 3,
      type: STEP,
      title: "Build apps in Dev Dashboard",
    },
    createCustomAppDev: {
      checked: false,
      disable: true,
      order: 4,
      type: STEP,
      title: "Create app",
    },
    appMarketcube: {
      checked: false,
      disable: true,
      order: 5,
      type: STEP,
      title: "Enter app name, For eg: 'Marketplace Integration'",
    },
    appCreate: {
      checked: false,
      disable: true,
      order: 6,
      type: STEP,
      title: "Click 'Create'",
    },
    appUri: {
      checked: false,
      disable: true,
      order: 7,
      type: STEP,
      title: "In App URL, copy and paste the below URL",
    },
    appUriValue: {
      checked: false,
      disable: true,
      order: 8,
      type: APP_URL_VALUE,
      title: "App URL",
      value: `https://${currentUri}`,
    },
    adminScopes: {
      checked: false,
      disable: true,
      order: 9,
      type: STEP,
      title: "In Scopes, copy and paste the below scopes",
    },

    adminScopesValues: {
      checked: false,
      disable: true,
      order: 10,
      type: SCOPE_VALUES,
      title: "Scopes",
      value:
        "write_customers, read_customers, write_discounts, read_discounts, write_fulfillments, read_fulfillments, write_inventory, read_inventory, write_locations, read_locations, write_merchant_managed_fulfillment_orders, read_merchant_managed_fulfillment_orders, write_order_edits, read_order_edits, write_orders, read_orders, write_product_listings, read_product_listings, write_products, read_products, write_shipping, read_shipping",
    },
    redirectUri: {
      checked: false,
      disable: true,
      order: 11,
      type: STEP,
      title:
        `In Redirect URL, add the URL you added in App URL above with '/shopify/callback' at the end. For eg: 'https://${currentUri}/shopify/callback'`,
    },
    redirectUriValue: {
      checked: false,
      disable: true,
      order: 12,
      type: REDIRECT_URL_VALUE,
      title: "Redirect URL",
      value: `https://${currentUri}/shopify/callback`,
    },
    releaseVersion: {
      checked: false,
      disable: true,
      order: 13,
      type: STEP,
      title: "Scroll down and click on 'Release' button",
    },
    creds: {
      checked: false,
      disable: true,
      order: 14,
      type: STEP,
      step: 2,
      title:
        "On the sidebar click on 'Settings' and then under 'Credentials' copy 'Client ID' and 'Secret Key', paste them in the below given fields",
    },
    enableDist: {
      checked: false,
      disable: true,
      order: 15,
      type: STEP,
      step: 3,
      title: {
        intro: "On the sidebar:",
        bullets: [
          "Click 'App Name'",
          "Under 'Distribution', click 'Select distribution method'",
          "Don’t see the Distribution option? Install the app directly from the Install app button on the app home page.",
          "Select 'Custom distribution'",
          "Enter your store domain in the given field and paste the same domain in the below given field (e.g. 'abcstore.myshopify.com')",
          "Click 'Generate link'",
        ],
      },
    },
    pasteUri: {
      checked: false,
      disable: true,
      order: 16,
      type: STEP,
      step: 3,
      title:
        "Paste the given link into a new tab and hit enter, it will redirect you to authenticate your store, select your store and then it will take you to install the app",
    },
    installApp: { checked: false, disable: true, order: 17, title: cms("common.appPermission.label.installApp") },
  };

  const productDiscoveryPermission = {
    goListingsPage: {
      checked: false,
      disable: false,
      order: 1,
      type: STEP,
      title: cms("common.appPermission.label.goListingsPage"),
    },
    developApps: {
      checked: false,
      disable: true,
      order: 2,
      type: STEP,
      title: cms("common.appPermission.label.developApps"),
    },
    customAppDev: {
      checked: false,
      disable: true,
      order: 3,
      type: STEP,
      title: cms("common.appPermission.label.customAppDev"),
    },
    appMarketcube: {
      checked: false,
      disable: true,
      order: 4,
      type: STEP,
      title: cms("common.appPermission.label.appMarketcubeVendor"),
    },
    adminScopes: {
      checked: false,
      disable: true,
      order: 5,
      type: STEP,
      title: cms("common.appPermission.label.adminScopes"),
    },
    write_inventory: {
      checked: false,
      disable: true,
      order: 9,
      type: SETTINGS,
      title: cms("common.appPermission.inventory.title"),
      helpText: "read_inventory",
    },
    read_locations: {
      checked: false,
      disable: true,
      order: 10,
      type: SETTINGS,
      title: cms("common.appPermission.locations.title"),
      helpText: "read_locations",
    },
    read_product_listings: {
      checked: false,
      disable: true,
      order: 14,
      type: SETTINGS,
      title: cms("common.appPermission.productListings.title"),
      helpText: "read_product_listings",
    },
    write_products: {
      checked: false,
      disable: true,
      order: 15,
      type: SETTINGS,
      title: cms("common.appPermission.products.title"),
      helpText: "read_products",
    },
    installApp: { checked: false, disable: true, order: 17, title: cms("common.appPermission.label.installApp") },
  };

  const isProductDiscovery = !!currentUserData?.isProductDiscovery;

  const [values, setValues] = useState(isProductDiscovery ? productDiscoveryPermission : defaultPermissions);

  const checklistConfig = useMemo(
    () => ({
      stepTypes: [STEP, SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE],
      stepType: STEP,
      valueDisplayTypes: [SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE],
      installAppKey: INSTALL_APP_KEY,
      adminScopesKey: ADMIN_SCOPES_KEY,
      settingsType: SETTINGS,
    }),
    [STEP, SCOPE_VALUES, APP_URL_VALUE, REDIRECT_URL_VALUE, SETTINGS, ADMIN_SCOPES_KEY, INSTALL_APP_KEY]
  );

  const persistChecklist = useCallback(
    (nextValues) => {
      if (shopConnectedStatus) {
        return;
      }
      if (saveChecklistTimeoutRef.current) {
        clearTimeout(saveChecklistTimeoutRef.current);
      }
      saveChecklistTimeoutRef.current = setTimeout(() => {
        saveConnectShopChecklist({
          variables: { input: { checklist: extractChecklistState(nextValues) } },
        });
      }, 400);
    },
    [saveConnectShopChecklist, shopConnectedStatus]
  );

  useEffect(() => {
    const baseValues = isProductDiscovery ? productDiscoveryPermission : defaultPermissions;
    if (!shopConnectedStatus && currentUserData?.connectShopChecklist) {
      setValues(applySavedChecklist(baseValues, currentUserData.connectShopChecklist, checklistConfig));
      return;
    }
    setValues(baseValues);
  }, [checklistConfig, currentUserData?.connectShopChecklist, isProductDiscovery, shopConnectedStatus]);

  useEffect(
    () => () => {
      if (saveChecklistTimeoutRef.current) {
        clearTimeout(saveChecklistTimeoutRef.current);
      }
    },
    []
  );

  const [apiDetails, setApiDetails] = useState({
    shopSubDomain: currentUserData?.shop ? currentUserData?.shop + ".myshopify.com" : "",
    apiKey: currentUserData?.storeConfig?.shopify?.clientId || "",
    apiSecretKey: currentUserData?.storeConfig?.shopify?.clientSecret || "",
  });

  const toggleBanner = () =>
    setBanner({
      action: null,
      isOpen: false,
      status: "",
      title: "",
      onDismiss: null,
    });

  const handleChange = useCallback(
    (value) => {
      if (shopConnectedStatus) {
        return;
      }
      if (value === "creds") {
        const apiKey = apiDetails?.apiKey?.trim();
        const apiSecretKey = apiDetails?.apiSecretKey?.trim();
        if (!apiKey || !apiSecretKey) {
          handleValidation(API_APP_KEY, apiDetails?.apiKey);
          handleValidation(API_APP_SECRET_KEY, apiDetails?.apiSecretKey);
          return;
        }
      }
      if (value === "enableDist") {
        const shopSubDomain = apiDetails?.shopSubDomain?.trim();
        if (!shopSubDomain) {
          handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain);
          return;
        }
        if (distributionMode === "fallback") {
          setValues((prevState) => {
            const nextValues = {
              ...prevState,
              enableDist: { ...prevState.enableDist, checked: true, disable: false },
              pasteUri: { ...prevState.pasteUri, checked: true, disable: false },
              installApp: { ...prevState.installApp, checked: true, disable: false },
            };
            persistChecklist(nextValues);
            return nextValues;
          });
          return;
        }
      }

      const updateData = computeChecklistUpdate(values, value, checklistConfig);

      setValues((prevState) => {
        const nextValues = { ...prevState, ...updateData };
        if (Object.keys(updateData).length) {
          persistChecklist(nextValues);
        }
        return nextValues;
      });
    },
    [
      API_APP_KEY,
      API_APP_SECRET_KEY,
      REGISTERSUBDOMIN,
      apiDetails,
      checklistConfig,
      distributionMode,
      persistChecklist,
      shopConnectedStatus,
      values,
    ]
  );

  const validShop = (shopCheck) => {
    if (!shopCheck) {
      return false;
    }

    const isValidShop = shopCheck.endsWith(".myshopify.com");
    const shopIndex = shopCheck.indexOf(".myshopify.com");

    if (shopIndex <= -1 || !isValidShop) {
      return shopCheck;
    }
    const shopName = shopCheck.slice(0, shopIndex);
    return shopName;
  };

  const handleField = (field, value) => {
    setApiDetails((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (appToMigrate) {
      setShopConnectedStatus(false);
    }
  }, [appToMigrate]);

  const handleValidation = async (field, value) => {
    const schema = {};

    const validationError = await validate(field, value, cms, schema);

    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };
  const isAnyError = () => Object.values(errorMessage).some((error) => error);

  const isAllValuesFilled = () =>
    Object.keys(values).every((key) => {
      if (values[key].checked === false) {
        return false;
      }
      return typeof values[key].checked === "boolean" ? true : values[key];
    });

  const isAllAPIValuesFilled = () =>
    Object.keys(apiDetails).every((key) => {
      if (!apiDetails[key]) {
        return false;
      }
      return typeof apiDetails[key] === "boolean" ? true : apiDetails[key];
    });

  const [toggleStatus, setToggleStatus] = useState({
    read_customers: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    read_discounts: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_fulfillments: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_inventory: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    read_locations: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_merchant_managed_fulfillment_orders: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_order_edits: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    read_orders: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    read_product_listings: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_products: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
    write_shipping: {
      active: true,
      toggleText: baseHelper.ucFirst(HIDE),
    },
  });

  const handleToggle = (event) => {
    const { id } = event.currentTarget;
    setToggleStatus((newToggleStatus) => ({
      ...newToggleStatus,
      [id]: {
        active: !newToggleStatus[id].active,
        toggleText: newToggleStatus[id].active ? SHOW : HIDE,
      },
    }));
  };

  const handleTermModal = (value) => {
    if (value === constant.CONNECT) {
      setIsOpen(true);
    }
    setErrorMessage("");
  };

  const onSubmit = async () => {
    try {
      let bannerValue = {};
      const payload = {
        shopSubDomain: apiDetails?.shopSubDomain.trim(),
        apiKey: apiDetails?.apiKey.trim(),
        apiSecretKey: apiDetails?.apiSecretKey.trim(),
      };
      const res = await newAppDetails({
        variables: {
          input: payload,
        },
      });

      const responseData = baseHelper.getResponseData(res.data, "newAppDetails");
      if (responseData) {
        window.location.href = `https://${payload.shopSubDomain}/admin/oauth/authorize?client_id=${payload.apiKey
          }&scope=${values.adminScopesValues.value}&redirect_uri=${values.redirectUriValue.value
          }&state=${Math.random().toString(36).substring(2, 15)}`;

        return null;
      }
      if (!responseData) {
        const errorResponse = baseHelper.getResponseError(res.data, "newAppDetails");
        bannerValue = { status: constant.CRITICAL, title: errorResponse, onDismiss: toggleBanner };
        setIsOpen(false);
      }

      setBanner({ ...bannerValue, isOpen: true });
      /// end some error come
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const resetForm = () => {
    setShopConnectedStatus(false);
    setDistributionMode("normal");
    const resetData = {};
    Object.keys(values).forEach((item) => {
      resetData[item] = { ...values[item], checked: false, disable: true };
    });
    resetData.goListingsPage = { ...values.goListingsPage, checked: false, disable: false };
    setValues(resetData);
    setApiDetails({ shopSubDomain: "", apiAccessToken: "", apiKey: "", apiSecretKey: "" });
  };
  useEffect(() => {
    if (disconnectStatus) {
      resetForm();
    }
  }, [disconnectStatus]);

  const secondaryActionsButton = () => {
    if (!shopConnectedStatus) {
      return [
        {
          content: cms("common.button.cancel"),
          id: "cancelButton",
          onAction: () => navigate("/products"),
        },
      ];
    }

    return [
      {
        content: (
          <ConnectedShop
            cms={cms}
            currentUserData={currentUserData}
            setBanner={setBanner}
            setIsConnected={setIsConnected}
            setShop={setShop}
            setDisconnectStatus={setDisconnectStatus}
          />
        ),
        disabled: !shopConnectedStatus,
        id: "disconnectButton",
        destructive: !shopConnectedStatus,
      },
    ];
  };
  // connected shop success message
  if (location.search === "?shop=true") {
    const bannerValues = {
      status: constant.SUCCESS,
      children: "",
      title: cms("common.appPermission.message.success.selectVendor"),
      onDismiss: toggleBanner,
    };
    setBanner({ ...bannerValues, isOpen: true });
    navigate("/products/add");
  }
  // connected shop
  const renderStepLabel = (title) => {
    if (title && typeof title === "object" && Array.isArray(title.bullets)) {
      return (
        <div>
          {title.intro ? <div>{title.intro}</div> : null}
          <ul style={{ paddingLeft: "15px", display: "flex", flexDirection: "column", gap: "5px", marginBottom: "0px" }}>
            {title.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
      );
    }
    return title;
  };

  if (shopConnectedStatus) {
    return (
      <Card title={cms("section.connect.title")}>
        <Card.Section sectioned>
          <BlockStack gap="2">{cms("section.connect.description")}</BlockStack>
          <br />

          <TextField
            label={cms("common.appPermission.shop.label")}
            error={errorMessage && errorMessage.shopSubDomain}
            disabled={!!shopConnectedStatus}
            // helpText={`${cms("common.appPermission.shop.helpText")}`}
            id="shopSubDomain"
            type={constant.TEXT}
            prefix={`${cms("common.appPermission.shop.prefix.shopSubDomain")}`}
            suffix={`${cms("common.appPermission.shop.suffix.shopSubDomain")}`}
            value={shopConnectedStatus ? currentUserData.shop || apiDetails?.shopSubDomain : apiDetails?.shopSubDomain}
            onChange={(value) => handleField(REGISTERSUBDOMIN, value)}
            onBlur={() => handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain)}
          />
          <br />
          <InlineStack align="space-between" blockAlign="center">
            <ConnectedShop
              cms={cms}
              currentUserData={currentUserData}
              setBanner={setBanner}
              setIsConnected={setIsConnected}
              setShop={setShop}
              setDisconnectStatus={setDisconnectStatus}
            />
            <SyncProduct navigate={navigate} setBanner={setBanner} cms={cms} />
          </InlineStack>
        </Card.Section>
      </Card>
    );
  }
  return (
    <>
      {isProductDiscovery ? (
        <DiscoveryTermModal
          cms={cms}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          shop={shop}
          setBanner={setBanner}
          loading={loading}
          onSubmit={onSubmit}
          item={constant.SHOPIFY_CAPITAL}
        />
      ) : (
        <TermModal
          cms={cms}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          shop={shop}
          setBanner={setBanner}
          loading={loading}
          onSubmit={onSubmit}
          item={constant.SHOPIFY_CAPITAL}
        />
      )}
      {pageName === SHOPIFY_CONNECT_FORM && (
        <>
          {" "}
          <BlockStack gap="2">
            {cms("section.connect.description")}
            <Link target="_blank" url={constant.ABOUT_VENDOR_APP_MIGRATE}>
              {` ${cms("common.appPermission.label.aboutURL")}`}
            </Link>
          </BlockStack>
          <br />
        </>
      )}
      <Card title="Steps to connect your Shopify Store">
        <Card.Section title="Step 1: Configure Shopify App">
          {values &&
            Object.keys(values)
              .filter((item) => item !== "creds" && item !== "enableDist" && item !== "pasteUri" && item !== "installApp")
              .map((item) => {
                if (values[item].type === STEP) {
                  return (
                    <div key={item}>
                      <Checkbox
                        value={item}
                        label={values?.[item]?.title}
                        disabled={shopConnectedStatus ? true : values?.[item]?.disable}
                        checked={shopConnectedStatus ? true : values?.[item]?.checked}
                        onChange={() => handleChange(item)}
                      />
                    </div>
                  );
                }
                if (values[item].type === SCOPE_VALUES) {
                  return (
                    <div key={item} style={{ margin: "10px 0px" }}>
                      <TextField
                        label={values?.[item]?.title}
                        readOnly
                        disabled={values?.[item]?.disable}
                        connectedRight={<CopyButton values={values} item={item} handleChange={handleChange} />}
                        value={values?.[item]?.value}
                      />
                    </div>
                  );
                }
                if (values[item].type === APP_URL_VALUE) {
                  return (
                    <div key={item} style={{ margin: "10px 0px" }}>
                      <TextField
                        label={values?.[item]?.title}
                        readOnly
                        disabled={values?.[item]?.disable}
                        connectedRight={<CopyButton values={values} item={item} handleChange={handleChange} />}
                        value={values?.[item]?.value}
                      />
                    </div>
                  );
                }
                if (values[item].type === REDIRECT_URL_VALUE) {
                  return (
                    <div key={item} style={{ margin: "10px 0px" }}>
                      <TextField
                        label={values?.[item]?.title}
                        readOnly
                        disabled={values?.[item]?.disable}
                        connectedRight={<CopyButton values={values} item={item} handleChange={handleChange} />}
                        value={values?.[item]?.value}
                      />
                    </div>
                  );
                }

                return null;
              })}
        </Card.Section>

        <Card.Section title="Step 2: Extract Credentials">
          {values &&
            Object.keys(values).map((item) => {
              if (values?.[item]?.step === 2) {
                return (
                  <div key={item}>
                    <Checkbox
                      value={item}
                      label={values?.[item]?.title}
                      disabled={shopConnectedStatus ? true : values?.[item]?.disable}
                      checked={shopConnectedStatus ? true : values?.[item]?.checked}
                      onChange={() => handleChange(item)}
                    />

                    <>
                      <div style={{ marginTop: "10px" }}>
                        <TextField
                          label="Client ID*"
                          id="apiKey"
                          disabled={values?.[item]?.disable}
                          type={constant.TEXT}
                          error={errorMessage && errorMessage.apiKey}
                          value={apiDetails?.apiKey}
                          onChange={(value) => handleField(API_APP_KEY, value)}
                          onBlur={() => handleValidation(API_APP_KEY, apiDetails?.apiKey)}
                        />
                      </div>
                      <br />
                      <div>
                        <TextField
                          label="Secret Key*"
                          id="apiSecretKey"
                          disabled={values?.[item]?.disable}
                          type={constant.TEXT}
                          error={errorMessage && errorMessage.apiSecretKey}
                          value={apiDetails?.apiSecretKey}
                          onChange={(value) => handleField(API_APP_SECRET_KEY, value)}
                          onBlur={() => handleValidation(API_APP_SECRET_KEY, apiDetails?.apiSecretKey)}
                        />
                      </div>
                      <br />
                    </>
                  </div>
                );
              }

              return null;
            })}
        </Card.Section>
        <Card.Section title="Step 3: Select Distribution ">
          {values?.enableDist && (
            <div style={{ marginBottom: "10px" }}>
              {distributionMode === "normal" ? (
                <Button
                  variant="primary"
                  disabled={shopConnectedStatus ? true : values?.enableDist?.disable}
                  onClick={() => setDistributionMode("fallback")}
                >
                  I can’t see Distribution
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setDistributionMode("normal");
                    setValues((prevState) => ({
                      ...prevState,
                      enableDist: { ...prevState.enableDist, checked: false, disable: false },
                      pasteUri: { ...prevState.pasteUri, checked: false, disable: true },
                      installApp: { ...prevState.installApp, checked: false, disable: true },
                    }));
                  }}
                >
                  Back to Distribution Steps
                </Button>
              )}
            </div>
          )}

          {distributionMode === "normal" &&
            values &&
            Object.keys(values).map((item) => {
              if (values?.[item]?.step === 3) {
                return (
                  <div key={item}>
                    <Checkbox
                      value={item}
                      label={renderStepLabel(values?.[item]?.title)}
                      disabled={shopConnectedStatus ? true : values?.[item]?.disable}
                      checked={shopConnectedStatus ? true : values?.[item]?.checked}
                      onChange={() => handleChange(item)}
                    />
                    {values?.[item]?.step === 3 && item === "enableDist" ? (
                      <div style={{ margin: "10px 0px" }}>
                        <TextField
                          label={`${cms("common.appPermission.shop.label")}*`}
                          error={errorMessage && errorMessage.shopSubDomain}
                          disabled={values?.[item]?.disable}
                          id="shopSubDomain"
                          type={constant.TEXT}
                          value={
                            shopConnectedStatus
                              ? currentUserData.shop || apiDetails?.shopSubDomain
                              : apiDetails?.shopSubDomain
                          }
                          onChange={(value) => handleField(REGISTERSUBDOMIN, value)}
                          onBlur={() => handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain)}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              }

              return null;
            })}
          {distributionMode === "fallback" && values?.enableDist && (
            <div>
              <Checkbox
                value="enableDist"
                label={renderStepLabel({
                  intro: "If Distribution is not visible:",
                  bullets: [
                    "Click 'Home'",
                    "Click 'Install app' on the app home page",
                    "Enter your store domain in the field below (e.g. 'abcstore.myshopify.com')",
                  ],
                })}
                disabled={shopConnectedStatus ? true : values?.enableDist?.disable}
                checked={shopConnectedStatus ? true : values?.enableDist?.checked}
                onChange={() => handleChange("enableDist")}
              />
              <div style={{ margin: "10px 0px" }}>
                <TextField
                  label={`${cms("common.appPermission.shop.label")}*`}
                  error={errorMessage && errorMessage.shopSubDomain}
                  disabled={values?.enableDist?.disable}
                  id="shopSubDomain"
                  type={constant.TEXT}
                  value={
                    shopConnectedStatus
                      ? currentUserData.shop || apiDetails?.shopSubDomain
                      : apiDetails?.shopSubDomain
                  }
                  onChange={(value) => handleField(REGISTERSUBDOMIN, value)}
                  onBlur={() => handleValidation(REGISTERSUBDOMIN, apiDetails?.shopSubDomain)}
                />
              </div>
            </div>
          )}
        </Card.Section>

        <Card.Section title="Step 4: Install App">
          <Checkbox
            value="installApp"
            label={values?.installApp?.title}
            disabled={shopConnectedStatus ? true : values?.installApp?.disable}
            checked={shopConnectedStatus ? true : values?.installApp?.checked}
            onChange={() => handleChange(INSTALL_APP_KEY)}
          />
        </Card.Section>
      </Card>
      <PageActions
        primaryAction={[
          {
            content: cms("common.button.submit"),
            id: "submitButton",
            onAction: () => setIsOpen(true),
            disabled: !isAllValuesFilled() || !isAllAPIValuesFilled() || isAnyError(),
            loading,
          },
        ]}
        secondaryActions={secondaryActionsButton()}
      />
      {
        pageName === SHOPIFY_CONNECT_FORM && shopConnectedStatus && (
          <InlineStack>
            <SyncProduct navigate={navigate} setBanner={setBanner} cms={cms} />
          </InlineStack>
        )
      }
    </>
  );
};

VendorAppConnectShopify.propTypes = {
  setBanner: PropTypes.func,
};

VendorAppConnectShopify.defaultProps = {
  setBanner: () => { },
};

export default withFeature(withErrorBoundary(VendorAppConnectShopify), {
  feature: constant.PRODUCT_IMPORT_SHOPIFY,
});
